import { ActionIcon, Button, Flex, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconFile, IconUpload, IconX } from "@tabler/icons-react";
import { type ChangeEvent, useCallback, useRef, useState } from "react";
import { apiService } from "../../services";
import { translateIdentity, type StaticTextTranslator } from "../../utils";
import styles from "./FileUpload.module.css";

const ALLOWED_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png", ".docx"];
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

const EXTENSION_CONTENT_TYPE_MAP: Record<string, string> = {
  pdf: "application/pdf",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

interface FileUploadProps {
  sessionId: string;
  questionId: string;
  onSubmit: (fileUrl: string, fileName: string) => void | Promise<void>;
  disabled?: boolean;
  translate?: StaticTextTranslator;
  /** Called when the upload starts (true) and finishes (false). Lets the
   * parent disable sibling controls (e.g. Skip) that would otherwise race
   * with submit — mirrors the Voice component's contract. */
  onUploadingChange?: (isUploading: boolean) => void;
}

function validateFile(
  file: File,
  translate: StaticTextTranslator,
): string | null {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return translate("File size exceeds the 10 MB limit");
  }

  const extension = `.${file.name.split(".").pop()?.toLowerCase()}`;
  if (!ALLOWED_EXTENSIONS.includes(extension)) {
    return `${translate("Unsupported file type. Allowed types:")} ${ALLOWED_EXTENSIONS.join(", ")}`;
  }

  return null;
}

export function FileUpload({
  sessionId,
  questionId,
  onSubmit,
  disabled = false,
  translate = translateIdentity,
  onUploadingChange,
}: FileUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const setUploadingState = useCallback(
    (uploading: boolean) => {
      setIsUploading(uploading);
      onUploadingChange?.(uploading);
    },
    [onUploadingChange],
  );

  const handleFileChange = useCallback(
    ({ currentTarget }: ChangeEvent<HTMLInputElement>) => {
      const { files } = currentTarget;
      const selectedFile = files?.[0];
      if (!selectedFile) {
        return;
      }

      const validationError = validateFile(selectedFile, translate);
      if (validationError) {
        currentTarget.value = "";
        notifications.show({
          title: translate("Upload failed"),
          message: validationError,
          color: "red",
        });
        return;
      }

      setFile(selectedFile);
    },
    [translate],
  );

  const resetSelection = useCallback(() => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleUpload = useCallback(async () => {
    if (!file) {
      return;
    }

    setUploadingState(true);

    try {
      const fileExtension = file.name.split(".").pop()?.toLowerCase() ?? "";
      const contentType = EXTENSION_CONTENT_TYPE_MAP[fileExtension] ?? "";
      const { uploadUrl, fields, fileUrl } =
        await apiService.generateFileUploadUrl(
          sessionId,
          questionId,
          file.name,
          fileExtension,
          contentType,
        );

      await apiService.uploadFileResponse(uploadUrl, fields, file);
      await onSubmit(fileUrl, file.name);
      resetSelection();
    } catch (error) {
      console.error("File upload failed:", error);
      notifications.show({
        title: translate("Upload failed"),
        message: translate(
          "There was a problem uploading your file. Please try again.",
        ),
        color: "red",
      });
    } finally {
      setUploadingState(false);
    }
  }, [
    file,
    onSubmit,
    questionId,
    resetSelection,
    sessionId,
    setUploadingState,
    translate,
  ]);

  return (
    <Flex align="flex-end" className={styles.container} direction="column">
      <input
        accept={ALLOWED_EXTENSIONS.join(",")}
        className={styles.hiddenInput}
        data-testid="file-upload-input"
        onChange={handleFileChange}
        ref={fileInputRef}
        type="file"
      />

      {file ? (
        <>
          <Flex align="center" className={styles.selectedRow} gap="sm">
            <ActionIcon
              aria-label={translate("Remove file")}
              disabled={disabled || isUploading}
              onClick={resetSelection}
              size="lg"
              variant="default"
            >
              <IconX size={18} />
            </ActionIcon>

            <Flex align="center" className={styles.fileChip} gap="xs">
              <IconFile size={18} />
              <Text className={styles.fileName}>{file.name}</Text>
            </Flex>
          </Flex>

          <Button
            className={styles.submitButton}
            data-testid="file-upload-done"
            disabled={disabled}
            leftSection={<IconCheck size={18} />}
            loading={isUploading}
            onClick={handleUpload}
          >
            {translate("Done uploading")}
          </Button>
        </>
      ) : (
        <>
          <Button
            disabled={disabled}
            onClick={() => fileInputRef.current?.click()}
            rightSection={<IconUpload size={18} />}
          >
            {translate("Select file to upload")}
          </Button>
          <Text c="dimmed" size="sm">
            {translate("Max size 10 MB")}
          </Text>
        </>
      )}
    </Flex>
  );
}
